import chroma from "chroma-js";
import _ from "lodash";
import { observer } from "mobx-react-lite";
import React, { useEffect } from "react";
import { createGlobalStyle } from "styled-components";
import { AppType } from "../constants/AppType";
import { device } from "../constants/mediaQueries";
import { getAppType } from "../getAppType";
import { useSettings } from "../settings/useSettings";
import { round } from "../utils/round";
import { getThemeSettingValue } from "./getThemeSettingValue";
import { iframeAvailableContentHeight } from "./IframeAvailableContentHeight";
import { useShouldShowDarkMode } from "../api/settings/useShouldShowDarkMode";

export const AppGlobalStyles = observer(() => {
  const settings = useSettings();

  const iframeVh = iframeAvailableContentHeight.get();
  const vh = iframeVh ? `${round(iframeVh * 0.01, 3)}px` : "1vh";

  const darkMode = useShouldShowDarkMode().darkMode;

  const primaryColor = darkMode
    ? settings.dt_primaryColor
    : settings.lt_primaryColor;
  const surfaceColor = darkMode
    ? settings.dt_surfaceColor
    : settings.lt_surfaceColor;
  const textColor = darkMode ? settings.dt_textColor : settings.lt_textColor;

  useEffect(() => {
    const pwaThemeColor = getComputedStyle(document.body)
      .getPropertyValue("--surface-a")
      .trim();

    document
      .getElementById("pwaThemeColor")
      ?.setAttribute("content", pwaThemeColor);
  }, [surfaceColor]);

  return (
    <_AppGlobalStyles
      fontSize={settings.fontSize}
      primaryColor={primaryColor}
      surfaceColor={surfaceColor}
      textColor={textColor}
      darkMode={darkMode}
      appType={getAppType()}
      vh={vh}
    />
  );
});

export interface AppGlobalStylesProps {
  fontSize: string;
  primaryColor: string;
  surfaceColor: string;
  textColor: string;
  darkMode: boolean;
  appType: AppType;
  vh: string;
}

const _AppGlobalStyles = createGlobalStyle<AppGlobalStylesProps>`

  :root:root {

    --standard-cvp-width: 1032px;
    --vh: ${(p) => p.vh};

     

    --color-strength-relative: ${(p) => (p.darkMode ? 20 : 80)}%;
    --popup-border-thickness: ${(p) => (p.darkMode ? 1 : 0)}px;

    ${(p) => {
      if (!p.darkMode) {
        return `
          --primary-50-relative: var(--primary-50);
          --primary-100-relative: var(--primary-100);
          --primary-200-relative: var(--primary-200);
          --primary-300-relative: var(--primary-300);
          /* removed 400 so that 500 becomes the middle */
          --primary-500-relative: var(--primary-500);
          --primary-600-relative: var(--primary-600);
          --primary-700-relative: var(--primary-700);
          --primary-800-relative: var(--primary-800);
          --primary-900-relative: var(--primary-900);
        `;
      }
      return `
          --primary-50-relative: color-mix(in srgb, var(--primary-900) 80%, var(--surface-0));
          --primary-100-relative: var(--primary-900);
          --primary-200-relative: var(--primary-800);
          --primary-300-relative: var(--primary-700);
          /* removed 400 so that 500 becomes the middle */
          --primary-500-relative: var(--primary-500);
          --primary-600-relative: var(--primary-300);
          --primary-700-relative: var(--primary-200);
          --primary-800-relative: var(--primary-100);
          --primary-900-relative: var(--primary-50);
        `;
    }}
  
    
    ${(p) => {
      // supply setting colors to the custom css variables generated by generateThemeFiles.js
      function getColor(key: string) {
        let color = p[key];
        if (!color) color = p.theme[key];

        const cssVariable = color.match(/var\(--([a-z-0-9,\s]+)\)/);
        if (cssVariable) {
          var asKebabCase = _.kebabCase(cssVariable[1] || "");
          color = asKebabCase ? p.theme[asKebabCase] : null;
        }

        if (!color || !chroma.valid(color)) {
          if (key === "primaryColor") {
            color = p.darkMode ? "#7FA9AB" : "#26658C";
          } else if (key === "surfaceColor") {
            color = p.darkMode ? "#0e1315" : "#F8F8F8";
          } else if (key === "textColor") {
            color = p.darkMode ? "#F8F8F8" : "#262626";
          } else {
            color = "#26658C";
          }
        }
        return color;
      }

      let primaryColor = getColor("primaryColor");
      let surfaceColor = getColor("surfaceColor");
      let textColor = getColor("textColor");

      const _remap: { [key: string]: string } = {};

      Object.entries(p.theme.replaceableColors).forEach(([key, replacable]) => {
        if (replacable.type === "primary") {
          // use primary color from settings but with alpha value from theme
          const c = chroma(primaryColor).alpha(chroma(replacable.rgba).alpha());
          _remap[key] = `rgba(${c.rgba()})`;
          return;
        }

        if (replacable.type === "text") {
          const c = chroma(textColor).alpha(chroma(replacable.rgba).alpha());
          _remap[key] = `rgba(${c.rgba()})`;
          return;
        }

        if (replacable.type === "surface") {
          const c = chroma(surfaceColor).alpha(chroma(replacable.rgba).alpha());
          _remap[key] = `rgba(${c.rgba()})`;
          return;
        }

        if (["primaryGradient", "hover", "active"].includes(replacable.type)) {
          // brighten/darken specified primary by approximately the same amount as the original colors
          let color = chroma(primaryColor);
          const T = replacable.transformationFromPrimary;

          if (T.type === "darken") color = chroma.mix(color, "#000", T.arg);
          if (T.type === "brighten") color = chroma.mix(color, "#fff", T.arg);

          _remap[key] = `rgba(${color.rgba()})`;
          return;
        }

        if (replacable.type === "surfaceGradient") {
          // brighten/darken specified primary by approximately the same amount as the original colors
          let color = chroma(surfaceColor);
          const T = replacable.transformationFromSurface;

          if (T.type === "darken") color = chroma.mix(color, "#000", T.arg);
          if (T.type === "brighten") color = chroma.mix(color, "#fff", T.arg);

          _remap[key] = `rgba(${color.rgba()})`;
          return;
        }
      });

      return Object.entries(_remap)
        .map(([key, val]) => `--${key}: ${val};\n`)
        .join("");
    }};

    ${(p) => {
      return Object.entries(p.theme.additionalCssVariables)
        .map(([key, val]) => `--${key}: ${val};\n`)
        .join("");
    }};

    ${(p) =>
      p.darkMode
        ? `--primary-color-text: #ffffff;
`
        : ""}

  }

  ${(p) =>
    p.darkMode
      ? `
  /* Lara dark themes default to dark text on bright primary; we use light primaries (#7FA9AB etc.) → white labels */
  body.cvp-body .p-button:not(.p-button-outlined):not(.p-button-text):not(.p-button-secondary):not(.p-button-contrast):not(.p-button-danger):not(.p-button-success):not(.p-button-info):not(.p-button-help):not(.p-button-warning):not(.p-button-link) {
    color: #ffffff;
  }
  body.cvp-body .p-button:not(.p-button-outlined):not(.p-button-text):not(.p-button-secondary):not(.p-button-contrast):not(.p-button-danger):not(.p-button-success):not(.p-button-info):not(.p-button-help):not(.p-button-warning):not(.p-button-link):not(:disabled):hover,
  body.cvp-body .p-button:not(.p-button-outlined):not(.p-button-text):not(.p-button-secondary):not(.p-button-contrast):not(.p-button-danger):not(.p-button-success):not(.p-button-info):not(.p-button-help):not(.p-button-warning):not(.p-button-link):not(:disabled):active {
    color: #ffffff;
  }
  body.cvp-body .p-selectbutton .p-button.p-highlight,
  body.cvp-body .p-selectbutton .p-button.p-highlight .p-button-icon-left,
  body.cvp-body .p-selectbutton .p-button.p-highlight .p-button-icon-right,
  body.cvp-body .p-togglebutton.p-highlight .p-button,
  body.cvp-body .p-togglebutton.p-highlight .p-button .p-button-icon-left,
  body.cvp-body .p-togglebutton.p-highlight .p-button .p-button-icon-right,
  body.cvp-body .p-togglebutton:not(.p-disabled):has(.p-togglebutton-input:hover).p-highlight .p-button,
  body.cvp-body .p-togglebutton:not(.p-disabled):has(.p-togglebutton-input:hover).p-highlight .p-button .p-button-icon-left,
  body.cvp-body .p-togglebutton:not(.p-disabled):has(.p-togglebutton-input:hover).p-highlight .p-button .p-button-icon-right {
    color: #ffffff;
  }
`
      : ""}

  /* base start */

  html {
    ${(p) => {
      if ([AppType.cvWebtempNo, AppType.iframe].includes(p.appType)) {
        return getThemeSettingValue(p, "fontSize");
      }

      return "";
    }};
  }

  #cvpreactroot {
    background-color: ${(p) =>
      p.appType === AppType.iframe ? "transparent" : "var(--surface-ground)"};
  }

  body.cvp-body {
    margin: 0;
    padding: 0;

    &::before {
      // prevent childrens margins from moving body
      // (moved body caused iframe resizer to measure wrong)
      clear: both;
      content: "";
      display: table;
    }
  }

  .p-component {
    box-sizing: border-box;
     
    * {
      box-sizing: border-box; 
    }

    &.p-component-overlay, &.cvp-app-container {
      line-height: normal;
    }
  }



  .cvp-flex-align-center {
    display: flex;
    align-items: center,
  }


  /* base end */

  /* Typography start*/



  .cvp-h0 {
    display: block;
    font-size: 1.6em;
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: 600;

    @media ${device.md} {
      font-size: 2em;
    }
  }


  .cvp-h1 {
    display: block;
    font-size: 1.3em;
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: 600;

    @media ${device.md} {
      font-size: 1.5em;
    }
  }

  .cvp-h2 {
    display: block;
    font-size: 1.1em;
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-start: 0;
    margin-inline-end: 0;
    font-weight: 600;

    @media ${device.md} {
    font-size: 1.3em;
    }
  }

  .cvp-h3 {
    display: block;
    font-size: 1em;
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: 600;

    @media ${device.md} {
    font-size: 1.2em;
}
  }

  .cvp-h4 {
    display: block;
    font-size: 0.9em;
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: 600;

    @media ${device.md} {
      font-size: 0.9em;
}
  }

  .cvp-h5 {
    display: block;
    font-size: 0.83em;
    margin-block-start: 1.67em;
    margin-block-end: 1.67em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }

  .cvp-h6 {
    display: block;
    font-size: 0.67em;
    margin-block-start: 2.33em;
    margin-block-end: 2.33em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }

   /* Typography end*/



  .cvp-awaiting-translation {
    visibility: hidden;
  }

  .p-dialog {
    background-color: var(--surface-a);
  }

  .cvp-confirmation-dialog {
    min-width: min(100%, 360px);

    .p-dialog-header {
      border-bottom: none;
      padding: 1.5rem 1rem 1rem 1.5rem;
    }

    .p-dialog-content {
      padding: 1rem 1.5rem;
    }

    .p-dialog-footer {
      border-top: none;
      padding: 1rem 1.5rem 1.5rem 1.5rem;

      .p-button:last-child {
        margin-right: 0;
      }
    }
  }

  .cvp-confirmation-dialog-no-icon {
    .p-confirm-dialog-message.p-confirm-dialog-message.p-confirm-dialog-message {
      margin-left: 0;
    }
  }

  .cv-multiselect-grow {
    .p-multiselect-label {
      display: flex;
      flex-wrap: wrap;
      padding-bottom: 0;

      .p-multiselect-token {
        margin-bottom: 0.25rem;
      }
    }
  }

  .cv-treeselect-empty {
    padding: 0.25rem;
  }

  .cv-treeselect-empty-no-message {
    visibility: hidden;
  }

  .cv-treeselect-grow {
    .p-treeselect-label:not(.p-placeholder) {
      display: flex;
      flex-wrap: wrap;
      padding-bottom: 0;

      .p-treeselect-token {
        border-radius: 3px;
        width: 100%;

        &:not(:last-child) {
          margin-bottom: 0.25rem;
        }
      }
    }
    
    .cve-treeselect-value-remove-button {
      margin-left: auto;
    }
  }

  .cve-treeselect-value-remove-button {
    cursor: pointer;
  }

  .p-button-icon-only {
    .p-button-label {
      display: none;
    }
  }

  .cvp-checkbox {
    label {
      cursor: pointer;
    }
  }

  .vpn-only.vpn-only.vpn-only {
    border: 1px dashed var(--red-500);
    color: var(--red-500);
  }

  .vpn-only-color {
    color: var(--red-500) !important;
  }

  .required {
    color: var(--red-400);
    margin-left: 0.25rem;
  
  }

          
  .p-card {
    color: var(--text-color);
  }

  .p-hidden-accessible {
    // fix to remove unwanted scrollbar on admin page
    top: 0;
  }

  .p-input-icon-left {
    .pi {
      margin-left: 0.5rem;
    }
  }

  /* temporary woraround for icons not showing, or not moving properly after scrollbar appears */
  .pi:before {
    --webkit-backface-visibility: unset;
    backface-visibility: unset;
  }

  .cvp-custom-scrollbar { 
    scrollbar-width: thin;
    scrollbar-color: var(--primary-400) var(--primary-50-relative);
  }
`;

export const UserProvidedStyle = createGlobalStyle<{
  css: string;
}>`
${(p) => p.css ?? ""}; 
`;
